import type { ApiFailure, FieldError } from "./types.js";

/**
 * Thrown by every `ApiClient` method for a non-2xx response (or a network
 * failure). `failure` is a normalised, typed view of whatever the server
 * sent — see `ApiFailure` — so callers can `switch` on `failure.kind`
 * instead of re-parsing status codes and error bodies at every call site.
 */
export class ApiRequestError extends Error {
  readonly status: number | null;
  readonly failure: ApiFailure;

  constructor(status: number | null, failure: ApiFailure) {
    super(describeFailure(failure));
    this.name = "ApiRequestError";
    this.status = status;
    this.failure = failure;
  }
}

function describeFailure(failure: ApiFailure): string {
  switch (failure.kind) {
    case "validation":
      return `validation failed: ${failure.errors.map((e) => `${e.path}:${e.code}`).join(", ")}`;
    case "unauthorized":
      return `unauthorized: ${failure.reason ?? "no session"}`;
    case "forbidden":
      return "forbidden";
    case "rate-limited":
      return "rate-limited";
    case "not-found":
      return "not found";
    case "conflict":
      return `conflict: ${failure.reason}`;
    case "network":
    case "unknown":
      return failure.message;
  }
}

function toFailure(status: number, body: unknown): ApiFailure {
  const record = body !== null && typeof body === "object" ? (body as Record<string, unknown>) : {};

  if (status === 400 && record["error"] === "validation-failed") {
    return { kind: "validation", errors: (record["details"] as FieldError[] | undefined) ?? [] };
  }
  // A wrong/inactive employee PIN comes back as 403 (an authorization failure
  // of one action), a session/login failure as 401 — but both use the same
  // `unauthorized`+`reason` body, so both map to the one `unauthorized` kind
  // `describeApiError` already turns into "Nepravilna koda." / "Seja je
  // potekla." The 403 deliberately does NOT trip the expired-session redirect
  // below (that is keyed on 401), so a mistyped code stays in the form.
  if ((status === 401 || status === 403) && record["error"] === "unauthorized") {
    const reason = record["reason"];
    return { kind: "unauthorized", ...(typeof reason === "string" ? { reason: reason as "invalid-code" | "inactive-employee" | "invalid-credentials" } : {}) };
  }
  if (status === 403) {
    return { kind: "forbidden" };
  }
  if (status === 429) {
    return { kind: "rate-limited" };
  }
  if (status === 404) {
    return { kind: "not-found" };
  }
  if (status === 409) {
    const reason = typeof record["reason"] === "string" ? record["reason"] : "unknown";
    const conflictingNumber = typeof record["conflictingNumber"] === "number" ? record["conflictingNumber"] : undefined;
    return { kind: "conflict", reason, ...(conflictingNumber !== undefined ? { conflictingNumber } : {}) };
  }
  return { kind: "unknown", message: `unexpected response (${status}): ${JSON.stringify(body)}` };
}

export interface ApiClient {
  readonly baseUrl: string;
  get<T>(path: string, query?: Record<string, string | number | boolean | undefined>): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  patch<T>(path: string, body?: unknown): Promise<T>;
  delete<T>(path: string, body?: unknown): Promise<T>;
}

function buildQueryString(query?: Record<string, string | number | boolean | undefined>): string {
  if (!query) return "";
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const qs = params.toString();
  return qs.length > 0 ? `?${qs}` : "";
}

/**
 * Creates an API client rooted at `baseUrl`.
 *
 * `baseUrl` is empty in production: the app is always served from the same
 * origin as its API (the hosted deployment serves both together; the
 * desktop shell's embedded server does the same — see `vite.config.ts`'s
 * doc comment), so plain relative `/api/...` paths are correct there, and
 * the Vite dev server proxies `/api` to the real backend. Tests pass the
 * ephemeral test server's origin instead (see `test-support/server.ts`).
 */
export interface ApiClientOptions {
  /**
   * Called when a request is rejected because the session is no longer valid.
   *
   * Deliberately excludes `/api/auth/*`: a rejected **login** is a wrong
   * password, not an expired session, and treating it as one would clear state
   * and bounce the operator mid-typing. `/api/auth/me` returning 401 during
   * startup is likewise just "not signed in yet", which the provider already
   * handles.
   *
   * Without this, a session expiring mid-shift surfaced as a failed query
   * inside whichever component happened to ask — a counter clerk would see one
   * broken panel and no explanation, rather than being returned to the login
   * screen.
   */
  onUnauthorized?: () => void;
}

export function createApiClient(baseUrl = "", clientOptions: ApiClientOptions = {}): ApiClient {
  /**
   * A real browser never needs this: same-origin `fetch` (the production
   * case — see this function's own doc comment) sends and stores the
   * session cookie automatically once `credentials: "same-origin"` is set
   * below, and `Set-Cookie` is a forbidden response header a browser's
   * `fetch` never exposes to JS in the first place — so `response.headers
   * .get("set-cookie")` always reads `null` there and this stays dead code.
   *
   * Node's `fetch` (undici), which is what runs this client under Vitest
   * (`test-support/server.ts` points `baseUrl` at a real HTTP server on a
   * different port, and jsdom does not itself implement `fetch`), is not a
   * browser: it exposes `Set-Cookie` but has no cookie jar at all, so
   * nothing would otherwise carry the session from a login response to the
   * next request. This is that jar, scoped to one `ApiClient` instance —
   * enough to let the auth workflow be tested end-to-end over real HTTP
   * without reimplementing cookie storage in test-only code.
   */
  let cookieJar: string | null = null;

  async function request<T>(
    method: string,
    path: string,
    options: { body?: unknown; query?: Record<string, string | number | boolean | undefined> } = {},
  ): Promise<T> {
    const url = `${baseUrl}${path}${buildQueryString(options.query)}`;

    let response: Response;
    try {
      response = await fetch(url, {
        method,
        credentials: "same-origin",
        headers: {
          ...(options.body !== undefined ? { "Content-Type": "application/json" } : {}),
          ...(cookieJar ? { Cookie: cookieJar } : {}),
        },
        ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      });
    } catch (cause) {
      throw new ApiRequestError(null, { kind: "network", message: cause instanceof Error ? cause.message : String(cause) });
    }

    const setCookie = typeof response.headers.getSetCookie === "function" ? response.headers.getSetCookie() : [];
    const sessionCookie = setCookie[0];
    if (sessionCookie) {
      const pair = sessionCookie.split(";")[0] ?? "";
      // An empty value is how `clearCookie` (logout, or the guard clearing a
      // dead session) shows up here — stop sending it rather than forward a
      // now-meaningless "sid=".
      cookieJar = pair.endsWith("=") ? null : pair;
    }

    const text = await response.text();
    const parsed: unknown = text.length > 0 ? JSON.parse(text) : undefined;

    if (!response.ok) {
      if (response.status === 401 && !path.startsWith("/api/auth/")) {
        clientOptions.onUnauthorized?.();
      }
      throw new ApiRequestError(response.status, toFailure(response.status, parsed));
    }
    return parsed as T;
  }

  return {
    baseUrl,
    get: (path, query) => request("GET", path, query !== undefined ? { query } : {}),
    post: (path, body) => request("POST", path, { body: body ?? {} }),
    patch: (path, body) => request("PATCH", path, { body: body ?? {} }),
    delete: (path, body) => request("DELETE", path, { body: body ?? {} }),
  };
}
